import { apiClient } from "./apiClient";
import { extractStatsArray } from "./utils/dataValidators";

/**
 * Obtiene las estadísticas del partido según el ID
 * @param {string} matchId - ID del partido
 * @param {string} country - País para filtrar datos (parámetro query)
 * @returns {Promise<Object>} Datos de estadísticas del partido
 */
export const getStats = async (matchId, country = null) => {
  try {
    const response = await apiClient.get(
      `/api/core-clarosports/v2/events/match/${matchId}/stats`,
      {
        params: {
          ...(country === 'us' && { country })
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching stats data:", error.message);
    throw error;
  }
};

/**
 * Transforma los datos del API de estadísticas a un formato compatible con los componentes
 * @param {Object} apiData - Datos crudos del API de estadísticas
 * @returns {Object} Estadísticas transformadas
 */
export const transformStatsData = (apiData) => {
  if (!apiData) return null;

  const statsArray = extractStatsArray(apiData);
  
  return {
    homeId: apiData.home_id,
    awayId: apiData.away_id,
    stats: statsArray.map((stat) => ({
      key: stat.key,
      label: stat.label,
      home: stat.home || 0,
      away: stat.away || 0,
    })),
  };
};

/**
 * Obtiene un stat específico por key
 * @param {Array} stats - Array de estadísticas
 * @param {string} key - Key de la estadística
 * @returns {Object|null} Estadística solicitada
 */
export const getStatByKey = (stats, key) => {
  if (!Array.isArray(stats)) return null;
  return stats.find((stat) => stat.key === key) || null;
};

/**
 * Obtiene estadísticas comparativas entre dos equipos
 * @param {Object} statsData - Datos de estadísticas transformadas
 * @returns {Object} Comparación de estadísticas
 */
export const getStatsComparison = (statsData) => {
  if (!statsData || !statsData.stats || !Array.isArray(statsData.stats)) {
    return null;
  }

  const comparison = {};
  statsData.stats.forEach((stat) => {
    comparison[stat.key] = {
      label: stat.label,
      home: stat.home,
      away: stat.away,
      diff: Math.abs(stat.home - stat.away),
      winner: stat.home > stat.away ? 'home' : stat.away > stat.home ? 'away' : 'draw',
    };
  });

  return comparison;
};

/**
 * Obtiene estadísticas de un equipo específico
 * @param {Object} statsData - Datos de estadísticas transformadas
 * @param {string} team - 'home' o 'away'
 * @returns {Object|null} Estadísticas del equipo solicitado
 */
export const getTeamStats = (statsData, team) => {
  if (!statsData || !statsData.stats || !Array.isArray(statsData.stats)) {
    return null;
  }

  const teamStats = {};
  statsData.stats.forEach((stat) => {
    teamStats[stat.key] = {
      label: stat.label,
      value: stat[team] || 0,
    };
  });

  return teamStats;
};

/**
 * Filtra estadísticas clave (principales)
 * @param {Object} statsData - Datos de estadísticas transformadas
 * @returns {Array} Estadísticas principales
 */
export const getKeyStats = (statsData) => {
  if (!statsData || !statsData.stats || !Array.isArray(statsData.stats)) {
    return [];
  }

  const keyStatKeys = [
    'possession',
    'shots',
    'shots_on_target',
    'fouls',
    'yellow_cards',
    'corners',
    'crosses',
  ];

  return statsData.stats.filter((stat) => keyStatKeys.includes(stat.key));
};
